import React, { SetStateAction, useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import iconClose from '../../images/icon-close.svg';
import { AppContext } from '../context/AppContextProvider';
import { LocationListProps, StaffFormProps, StaffUpdate } from './StaffManagement';
import {
    addStaffMember,
    getStaffDetail,
    getStaffDetailyEmail,
    updateStaff,
    UserResponse,
} from '../../redux/staff-management/action/staff.action';
import { AppDispatch } from '../../redux/rootReducer';
import { useDispatch } from 'react-redux';
import { RoleProps, ToastType } from '../roles/Roles';
import TextInput from '../common/input/textInput/TextInput';
import Select, { SelectItemRenderer, SelectRenderer } from 'react-dropdown-select';
import Button from '../common/button/Button';
import Loader from '../common/loader/Loader';
import styles from './StaffManagement.module.css';
import { REGEX } from '../../constants/constants';
import { Option } from '../auth/login/Login';
import SearchInput from '../common/search-input/SearchInput';

interface StaffModalProps {
    selectedId: string;
    setShowModal: React.Dispatch<SetStateAction<boolean>>;
    setSelectedId: React.Dispatch<SetStateAction<string | null>>;
    setShowToast: React.Dispatch<SetStateAction<boolean>>;
    setToastMessage: React.Dispatch<SetStateAction<string | null>>;
    setToastType: React.Dispatch<SetStateAction<ToastType>>;
    onSuccess: () => void;
    locationList: LocationListProps[];
    onClose: () => void;
    rolesList: RoleProps[];
}

interface FormValues {
    fullName: string;
    email: string;
    locationId: string[];
    roleId: string[];
}

export const StaffModal = ({
    selectedId,
    setToastMessage,
    setShowModal,
    setToastType,
    setShowToast,
    onClose,
    locationList,
    rolesList,
    onSuccess,
}: StaffModalProps) => {
    const appContext = useContext(AppContext);
    const [isLoading, setIsLoading] = useState(false);
    const dispatch: AppDispatch = useDispatch();
    const [existingNonModifyLocationId, setExistingNonModifyLocationId] = useState<string[]>([]);
    const [existingLocationId, setExistingLocationId] = useState<string[]>([]);
    const [initialValues, setInitialValues] = useState<FormValues>({ fullName: '', email: '', locationId: [], roleId: [] });
    const [isFocused, setIsFocused] = useState(false);

    const locationOptions: Option[] = locationList.map((loc) => ({
        value: loc.id,
        label: loc.name,
    }));

    const {
        handleSubmit,
        setValue,
        getValues,
        formState: { isValid, errors },
        control,
        reset,
        watch,
    } = useForm<FormValues>({
        defaultValues: {
            fullName: '',
            email: '',
            locationId: [],
            roleId: [],
        },
        mode: 'onChange',
    });

    const onCloseModal = () => {
        setShowModal(false);
        onClose();
        reset();
    };

    const onAddSuccess = (message: string) => {
        setShowToast(true);
        setToastType('success');
        setToastMessage(message);
        setIsLoading(false);
        setShowModal(false);
        onSuccess();
    };

    const onAddFailure = (message: string) => {
        setShowToast(true);
        setToastType('error');
        setToastMessage(message);
        setIsLoading(false);
    };

    const onUpdateStaffSuccess = (message: string) => {
        setToastMessage(message);
        setToastType('success');
        setShowToast(true);
        setIsLoading(false);
        setShowModal(false);
        onSuccess();
    };

    const onUpdateStaffFailure = (message: string) => {
        setToastMessage(message);
        setToastType('error');
        setShowToast(true);
        setIsLoading(false);
    };

    const onStaffDetailSuccess = (data: UserResponse) => {
        setIsLoading(false);
        setShowModal(true);
        setValue('email', data?.email);
        setValue('fullName', data?.name);
        const locationIds = data.invitedBy
            ?.flatMap((user) =>
                user.locations
                    .filter((location) => locationList.some((loc) => loc.id === location.locationId))
                    .map((location) => location.locationId),
            )
            .filter(Boolean) as string[];
        const existNonModifyIds = data.invitedBy
            ?.flatMap((user) =>
                user.locations
                    .filter((location) => locationList.some((loc) => loc.id !== location.locationId))
                    .map((location) => location.locationId),
            )
            .filter(Boolean) as string[];
        setExistingLocationId(locationIds);
        setExistingNonModifyLocationId(existNonModifyIds);
        setValue('locationId', locationIds);
        setValue('roleId', data?.roleId ? [data.roleId] : []);

        const initialValues = {
            fullName: data?.name || '',
            email: data?.email || '',
            locationId: locationIds || [],
            roleId: data?.roleId ? [data.roleId] : [],
        };

        setInitialValues(initialValues);
    };

    const onStaffDetailFailure = () => {
        setIsLoading(false);
    };

    useEffect(() => {
        if (selectedId) {
            setIsLoading(true);
            dispatch(getStaffDetail(selectedId, onStaffDetailSuccess, onStaffDetailFailure));
        }
    }, [selectedId]);

    const onSubmit = (data: FormValues) => {
        if (appContext?.userInfo?.id) {
            if (selectedId) {
                setIsLoading(true);
                const newLocationIds = data?.locationId.filter((id: string) => !existingLocationId.includes(id));
                const removedLocationIDs = existingLocationId.filter(
                    (id: string) => ![...data.locationId, existingNonModifyLocationId].includes(id),
                );
                const staffObject: StaffUpdate = {
                    staffId: selectedId,
                    locationIdsToAdd: newLocationIds || [],
                    locationIdsToRemove: removedLocationIDs,
                    roleId: data?.roleId[0],
                    fullName: data?.fullName,
                };
                dispatch(updateStaff(staffObject, onUpdateStaffSuccess, onUpdateStaffFailure));
            } else {
                setIsLoading(true);
                const StaffObject: StaffFormProps = {
                    ...data,
                    roleId: data?.roleId[0],
                    adminId: appContext?.userInfo?.id,
                    staffId: null,
                };
                dispatch(addStaffMember(StaffObject, onAddSuccess, onAddFailure));
            }
        }
    };

    const currentValues = watch();
    const isFormUnchanged = JSON.stringify(initialValues) !== JSON.stringify(currentValues);

    useEffect(() => {
        if (!isFocused && REGEX.EMAIL.test(getValues('email'))) {
            setIsLoading(true);
            dispatch(getStaffDetailyEmail(getValues('email'), onStaffDetailSuccess, onStaffDetailFailure));
        }
    }, [isFocused]);

    const customItemRenderer = ({ item, itemIndex, props, state, methods }: SelectItemRenderer<Option>) => {
        return (
            <div className={'item-box'} key={itemIndex}>
                <input type="checkbox" id={item?.value} />
                <label htmlFor={item?.value}>{item?.value}</label>
            </div>
        );
    };

    const customContentRenderer = ({ props, state, methods }: SelectRenderer<Option>) =>
        state.values?.length ? (
            <div style={{ cursor: 'pointer' }}>
                {state.values.length} of {props.options.length} Properties selected
            </div>
        ) : (
            <div className={styles.placeholder}>
                <span>Select Properties</span>
            </div>
        );

    const customDropdownRenderer = ({ props, state, methods }: SelectRenderer<Option>) => {
        const regexp = new RegExp(state.search, 'i');
        return (
            <div className={styles.customDropdownMenu}>
                {locationOptions?.length && (
                    <div className={styles.dropdownHeader}>
                        <SearchInput
                            style={{ maxWidth: '360px', width: '100%' }}
                            value={state.search}
                            placeholder="Search Properties"
                            onChange={(e) => {
                                methods.setSearch(e);
                            }}
                        />
                        <div className={styles.inlineItems}>
                            <div className={styles.boxes}>
                                <input
                                    type="checkbox"
                                    id="selectAll"
                                    checked={state.values.length === props.options.length && props.options.length > 0}
                                    onChange={(e) => {
                                        e?.target?.checked ? methods?.selectAll() : methods?.clearAll();
                                    }}
                                />
                                <label htmlFor="selectAll">Select All</label>
                            </div>
                            <button
                                className={styles.clearBtn}
                                type="button"
                                onClick={() => {
                                    methods.clearAll();
                                }}>
                                Clear All
                            </button>
                        </div>
                    </div>
                )}

                <div className={styles.listContainer}>
                    {props.options
                        .filter((option) => regexp.test(option.label))
                        .map((option: Option, index: number) => {
                            const isSelected = state.values.some((value) => JSON.stringify(value) === JSON.stringify(option));
                            if (!props.keepSelectedInList && isSelected) {
                                return null;
                            }

                            return (
                                <div key={index} className={styles.boxes} onChange={() => methods.addItem(option)}>
                                    <input type="checkbox" id={option.value} checked={isSelected} />
                                    <label htmlFor={option.value}>{option.label}</label>
                                </div>
                            );
                        })}
                </div>
            </div>
        );
    };

    return (
        <div className={styles.modalInnerContainer}>
            {isLoading && <Loader />}
            <div className={styles.modalHeaderContainer}>
                <h2 className={styles.modalHeader}>{selectedId ? `Edit Staff` : 'Add staff'}</h2>
                <div className={`${styles.close} ${isLoading ? styles.disabled : ''}`}>
                    <img src={iconClose} onClick={() => (!isLoading ? onCloseModal() : null)} alt="Close" />
                </div>
            </div>

            <div className={styles.modalBodyContainer}>
                <form
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                        }
                    }}
                    onSubmit={handleSubmit(onSubmit)}>
                    <Controller
                        name="fullName"
                        control={control}
                        rules={{
                            required: 'Name is required',
                        }}
                        render={({ field: { onChange, value } }) => {
                            return (
                                <div className={styles.fieldWrapper}>
                                    <TextInput
                                        name="fullName"
                                        autoFocus
                                        id="fullName"
                                        placeholder="Enter Name"
                                        onChange={onChange}
                                        value={value}
                                        error={errors?.fullName && errors?.fullName?.message}
                                    />
                                </div>
                            );
                        }}
                    />

                    <Controller
                        name="email"
                        control={control}
                        rules={{
                            required: 'Email is required',
                        }}
                        render={({ field: { onChange, value } }) => {
                            return (
                                <div className={styles.fieldWrapper}>
                                    <TextInput
                                        name="email"
                                        id="email"
                                        type="email"
                                        placeholder="Enter Email Address"
                                        onChange={onChange}
                                        onFocus={() => setIsFocused(true)}
                                        onBlur={() => setIsFocused(false)}
                                        value={value}
                                        error={errors?.email && errors?.email?.message}
                                    />
                                </div>
                            );
                        }}
                    />

                    <Controller
                        name="locationId"
                        control={control}
                        rules={{ required: selectedId ? false : 'Role selection is required' }}
                        render={({ field }) => (
                            <div className={`${styles.fieldWrapper} ${styles.fullWidth}`}>
                                <Select
                                    className={`${styles.multiSelect} custom-mult-select-dropdown`}
                                    options={locationOptions || []}
                                    itemRenderer={customItemRenderer}
                                    contentRenderer={customContentRenderer}
                                    dropdownRenderer={customDropdownRenderer}
                                    closeOnClickInput
                                    searchable
                                    selectAll
                                    // keepSelectedInList
                                    clearable
                                    onSelect={() => undefined}
                                    onDeselect={() => undefined}
                                    placeholder="Select Properties"
                                    searchBy="label"
                                    wrapperClassName="custom-mult-select-dropdown"
                                    selectAllLabel="Select All Properties"
                                    addPlaceholder="Select Properties"
                                    values={locationOptions.filter((loc: Option) => field.value?.includes(loc.value))} // Correctly filter selected values
                                    onChange={(selected: Option[]) => field.onChange(selected.map((s) => s.value))} // Extract only IDs
                                    multi={true}
                                />
                            </div>
                        )}
                    />

                    <Controller
                        name="roleId"
                        control={control}
                        rules={{ required: 'Role selection is required' }}
                        render={({ field }) => (
                            <div className={`${styles.fieldWrapper} ${styles.fullWidth}`}>
                                <Select
                                    className={`${styles.multiSelect} custom-mult-select-dropdown`}
                                    options={rolesList || []}
                                    labelField="name"
                                    searchable
                                    placeholder="Select Role"
                                    searchBy="name"
                                    onSelect={() => undefined}
                                    onDeselect={() => undefined}
                                    valueField="id"
                                    values={rolesList.filter((role: RoleProps) => field.value?.includes(role.id))}
                                    onChange={(selected: LocationListProps[]) => field.onChange(selected.map((s) => s.id))}
                                    multi={false}
                                />
                            </div>
                        )}
                    />

                    <div className={styles.modalFooter}>
                        <Button onClick={onCloseModal} style={{ minWidth: 100 }} variant="outlined-green" disabled={isLoading}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="green"
                            style={{ minWidth: 100 }}
                            disabled={isLoading || !isValid || !isFormUnchanged}>
                            {selectedId ? 'Update' : 'Add'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};
